import { useTranslation } from "react-i18next"
import { CheckCircle, Info, Warning } from "@phosphor-icons/react"
import { cva, type VariantProps } from "class-variance-authority"
import { Button, ButtonVariant } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export const bannerVariants = cva("relative flex w-full items-center justify-start text-sm [&>a]:underline", {
  variants: {
    type: {
      "full-width": "min-h-12 gap-2 px-8 py-3",
      inline: "min-h-10 gap-1 rounded-md px-4 py-2",
    },
    variant: {
      info: "bg-surface-subdued text-on-surface",
      success: "bg-success-surface-subdued text-on-success-surface",
      warning: "bg-warning-surface-subdued text-on-warning-surface",
      critical: "bg-critical-surface text-on-critical-surface",
      "most-critical": "bg-critical text-on-critical",
    },
  },
  defaultVariants: {
    type: "full-width",
    variant: "info",
  },
})

const Icon = ({ variant }: { variant: VariantProps<typeof bannerVariants>["variant"] }) => {
  switch (variant) {
    case "success":
      return <CheckCircle size={24} />
    case "warning":
      return <Warning size={24} />
    case "critical":
    case "most-critical":
      return <Warning weight="fill" size={24} />
    case "info":
    case undefined:
    case null:
      return <Info size={24} className="text-on-surface-subdued" />
  }
}

type DismissOptions = {
  label?: string
  className?: string
  variant?: ButtonVariant
  onDismiss: () => void
}

type BannerProps = { hasIcon?: boolean; iconClassName?: string; dismissOptions?: DismissOptions } & VariantProps<
  typeof bannerVariants
> &
  React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }

export const Banner = (props: BannerProps) => {
  const { t } = useTranslation()
  const { className, iconClassName, variant, type, children, hasIcon = true, ref, dismissOptions, ...rest } = props
  return (
    <div ref={ref} className={cn(bannerVariants({ variant, type }), className)} {...rest}>
      {hasIcon && (
        <div className={cn("self-start", iconClassName)}>
          <Icon variant={variant} />
        </div>
      )}
      {children}
      {dismissOptions && (
        <Button
          variant={dismissOptions.variant ?? "tertiary"}
          className={cn("-mr-3 ml-auto text-inherit", dismissOptions.className)}
          onClick={() => dismissOptions.onDismiss()}>
          {dismissOptions.label ?? t("common.button.dismiss")}
        </Button>
      )}
    </div>
  )
}
